// Global Instructions Rule Applied!
//
// Commitment forms HTTP calls — the three reads, each validated against its
// Zod model before it reaches a form:
//   GET /funds/commitments/:id            → view__fund_commitment_detail
//   GET /fund-groups/subscriptions/:id    → view__fund_group_commitment_detail
//   GET /funds/commitment-form-options    → view__commitment_form_options
// The writes stay where they were: saveFundCommitment / trashFundCommitment
// (funds/services/fundsService.ts) and saveFundGroupSubscription /
// deleteFundGroupSubscription (fund_groups/services/fundGroupsService.ts).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  type CommitmentFormOptions,
  type FundCommitmentDetail,
  type FundGroupCommitmentDetail,
  validateCommitmentFormOptions,
  validateFundCommitmentDetail,
  validateFundGroupCommitmentDetail
} from '../../../state_manager/data_models/commitment_form.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

const authHeaders = (accessToken: string | null): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {})
})

type ErrorBody = { data?: unknown; message?: string; error?: string }

const readError = (body: ErrorBody, status: number, fallback: string): string => {
  if (status === 401) return 'Authentication failed - please log in again'
  return body.message || body.error || `${fallback} (${status})`
}

export const fetchFundCommitmentDetail = async (
  fundCommitmentId: string,
  accessToken: string | null = null
): Promise<{ data: FundCommitmentDetail | null; notFound?: boolean; error?: string }> => {
  try {
    const route = API_ROUTES.FUNDS.COMMITMENT_GET
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, { id: fundCommitmentId }, {}), {
      method: route.method,
      headers: authHeaders(accessToken)
    })
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (response.status === 404) return { data: null, notFound: true }
    if (!response.ok) throw new Error(readError(body, response.status, 'Failed to fetch the commitment'))
    return { data: validateFundCommitmentDetail(body.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching fund commitment detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

export const fetchFundGroupCommitmentDetail = async (
  fundGroupCommitmentId: string,
  accessToken: string | null = null
): Promise<{ data: FundGroupCommitmentDetail | null; notFound?: boolean; error?: string }> => {
  try {
    const route = API_ROUTES.FUND_GROUPS.GET_SUBSCRIPTION
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, { id: fundGroupCommitmentId }, {}), {
      method: route.method,
      headers: authHeaders(accessToken)
    })
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (response.status === 404) return { data: null, notFound: true }
    if (!response.ok) throw new Error(readError(body, response.status, 'Failed to fetch the consolidated commitment'))
    return { data: validateFundGroupCommitmentDetail(body.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching consolidated commitment detail:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}

/** The forms' select lists. `accountId` scopes the investors list to that
 *  account (none without one); the rest does not depend on it. */
export const fetchCommitmentFormOptions = async (
  accountId: string | null,
  accessToken: string | null = null
): Promise<{ data: CommitmentFormOptions | null; error?: string }> => {
  try {
    const route = API_ROUTES.FUNDS.COMMITMENT_FORM_OPTIONS
    const query: Record<string, string> = accountId ? { account_id: accountId } : {}
    const response = await fetch(buildFullUrl(BACKEND_BASE_URL, route, {}, query), {
      method: route.method,
      headers: authHeaders(accessToken)
    })
    const body = (await response.json().catch(() => ({}))) as ErrorBody
    if (!response.ok) throw new Error(readError(body, response.status, 'Failed to fetch the commitment form options'))
    return { data: validateCommitmentFormOptions(body.data) }
  } catch (error) {
    if (process.env.NODE_ENV === 'development') console.error('Error fetching commitment form options:', error)
    return { data: null, error: error instanceof Error ? error.message : 'An unexpected error occurred' }
  }
}
